import React, { useState, useEffect } from 'react';
import { Plus, Search, Edit2, Trash2, RefreshCw, Target, Brain, TrendingUp, Link2, CheckCircle } from 'lucide-react';
import { toast } from 'react-hot-toast';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Modal from '../../components/ui/Modal';
import Loader from '../../components/ui/Loader';
import perfilService from '../../services/perfilService';
import examenService from '../../services/examenService';
import perfilPruebaService from '../../services/perfilPruebaService';
import discService from '../../services/discService';
import ipvService from '../../services/ipvService';

export default function Perfiles() {
  const [loading, setLoading] = useState(true);
  const [perfiles, setPerfiles] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  
  // Estado de paginación
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize] = useState(5); // DEFAULT_PAGE_SIZE = 5 según el backend
  const [totalPages, setTotalPages] = useState(0);
  const [totalElements, setTotalElements] = useState(0);
  const [sortBy, setSortBy] = useState(null); // Sin ordenamiento inicial
  const [direction, setDirection] = useState('asc');
  
  // Exámenes maestros (solo hay 1 de cada tipo)
  const [examenRaven, setExamenRaven] = useState(null);
  const [examenDisc, setExamenDisc] = useState(null);
  const [examenIpv, setExamenIpv] = useState(null);
  const [perfilesAsociados, setPerfilesAsociados] = useState({}); // {perfilId: {raven: bool, disc: bool, ipv: bool}}

  useEffect(() => {
    loadData(currentPage, sortBy, direction);
  }, []);

  const loadData = async (page = currentPage, sort = sortBy, dir = direction) => {
    try {
      setLoading(true);
      setPerfiles([]);

      // Cargar perfiles con paginación
      try {
        const params = {
          page,
          size: pageSize
        };
        
        if (sort && dir) {
          params.sortBy = sort;
          params.direction = dir;
        }
        
        const perfilesResponse = await perfilService.getAllPerfiles(params);
        console.log('Respuesta de perfiles:', perfilesResponse);
        
        // Manejar respuesta paginada o directa
        let perfilesData = [];
        if (Array.isArray(perfilesResponse)) {
          // Respuesta directa (array)
          perfilesData = perfilesResponse;
          setTotalPages(1);
          setTotalElements(perfilesResponse.length);
        } else if (perfilesResponse && perfilesResponse.content) {
          // Respuesta paginada
          perfilesData = perfilesResponse.content;
          setTotalPages(perfilesResponse.totalPages || 0);
          setTotalElements(perfilesResponse.totalElements || 0);
        } else {
          // Respuesta inesperada
          console.warn('Respuesta inesperada de perfiles:', perfilesResponse);
          perfilesData = [];
          setTotalPages(0);
          setTotalElements(0);
        }
        
        console.log('Datos de perfiles procesados:', perfilesData);
        setPerfiles(Array.isArray(perfilesData) ? perfilesData : []);
      } catch (error) {
        console.error('Error al cargar perfiles:', error);
        if (error.response?.status !== 400) {
          toast.error('Error al cargar perfiles');
        }
        setPerfiles([]);
        setTotalPages(0);
        setTotalElements(0);
      }

      // Cargar examen Raven maestro (el primero que exista)
      try {
        const examenesRaven = await examenService.getAllExamenes();
        if (examenesRaven && examenesRaven.length > 0) {
          setExamenRaven(examenesRaven[0]);
        }
      } catch (error) {
        console.error('Error cargando Raven:', error);
      }

      // Cargar examen DISC maestro
      try {
        const examenesDISC = await discService.listarDiscExamenes();
        if (examenesDISC && examenesDISC.length > 0) {
          setExamenDisc(examenesDISC[0]);
        }
      } catch (error) {
        console.error('Error cargando DISC:', error);
      }

      // Cargar examen IPV maestro
      try {
        const examenesIPV = await ipvService.listarExamenes();
        if (examenesIPV && examenesIPV.length > 0) {
          setExamenIpv(examenesIPV[0]);
        }
      } catch (error) {
        console.error('Error cargando IPV:', error);
      }

      // Cargar asociaciones existentes
      try {
        const asociaciones = await perfilPruebaService.getAll();
        const mapeo = {};
        asociaciones.forEach(asoc => {
          if (!mapeo[asoc.perfilId]) {
            mapeo[asoc.perfilId] = { raven: false, disc: false, ipv: false };
          }
          if (asoc.examenRavenId) mapeo[asoc.perfilId].raven = true;
          if (asoc.testDiscId || asoc.discExamenId) mapeo[asoc.perfilId].disc = true;
          if (asoc.ipvExamenId) mapeo[asoc.perfilId].ipv = true;
        });
        setPerfilesAsociados(mapeo);
      } catch (error) {
        console.error('Error cargando asociaciones:', error);
      }
    } finally {
      setLoading(false);
    }
  };

  // Funciones de paginación
  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
    loadData(newPage, sortBy, direction);
  };

  const handleSort = (newSortBy) => {
    const newDirection = (sortBy === newSortBy && direction === 'asc') ? 'desc' : 'asc';
    setSortBy(newSortBy);
    setDirection(newDirection);
    setCurrentPage(0); // Reset to first page when sorting
    loadData(0, newSortBy, newDirection);
  };

  const handleSave = async (data, examenesSeleccionados) => {
    try {
      let perfilId;
      
      if (editingItem) {
        await perfilService.updatePerfil(editingItem.id, data);
        perfilId = editingItem.id;
        toast.success('Perfil actualizado');
      } else {
        const nuevoPerfil = await perfilService.createPerfil(data);
        perfilId = nuevoPerfil?.id;
        toast.success('Perfil creado');
      }

      // Gestionar asociaciones de exámenes
      if (perfilId && examenesSeleccionados) {
        // Obtener todas las asociaciones actuales para eliminar las que se desmarcaron
        const todasLasAsociaciones = await perfilPruebaService.getAll();
        const asociacionesPerfil = todasLasAsociaciones.filter(a => a.perfilId === perfilId);

        // RAVEN: Agregar o quitar
        const asociacionRavenExistente = asociacionesPerfil.find(a => a.examenRavenId != null);
        if (examenesSeleccionados.raven && examenRaven) {
          // Quiere tener Raven
          if (!asociacionRavenExistente) {
            try {
              await perfilPruebaService.asociarExamenRaven(perfilId, examenRaven.id);
            } catch (err) {
              console.log('Raven ya asociado o error:', err);
            }
          }
        } else {
          // NO quiere tener Raven
          if (asociacionRavenExistente) {
            try {
              await perfilPruebaService.delete(asociacionRavenExistente.id);
            } catch (err) {
              console.log('Error eliminando Raven:', err);
            }
          }
        }

        // DISC: Agregar o quitar
        const asociacionDiscExistente = asociacionesPerfil.find(a => a.testDiscId != null || a.discExamenId != null);
        if (examenesSeleccionados.disc && examenDisc) {
          // Quiere tener DISC
          if (!asociacionDiscExistente) {
            try {
              await perfilPruebaService.asociarDiscExamen(perfilId, examenDisc.id);
            } catch (err) {
              console.log('DISC ya asociado o error:', err);
            }
          }
        } else {
          // NO quiere tener DISC
          if (asociacionDiscExistente) {
            try {
              await perfilPruebaService.delete(asociacionDiscExistente.id);
            } catch (err) {
              console.log('Error eliminando DISC:', err);
            }
          }
        }

        // IPV: Agregar o quitar
        const asociacionIpvExistente = asociacionesPerfil.find(a => a.ipvExamenId != null);
        if (examenesSeleccionados.ipv && examenIpv) {
          // Quiere tener IPV
          if (!asociacionIpvExistente) {
            try {
              await perfilPruebaService.asociarIpvExamen(perfilId, examenIpv.id);
            } catch (err) {
              console.log('IPV ya asociado o error:', err);
            }
          }
        } else {
          // NO quiere tener IPV
          if (asociacionIpvExistente) {
            try {
              await perfilPruebaService.delete(asociacionIpvExistente.id);
            } catch (err) {
              console.log('Error eliminando IPV:', err);
            }
          }
        }
      }

      loadData(currentPage, sortBy, direction);
      closeModal();
    } catch (error) {
      toast.error('Error al guardar');
      console.error(error);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('¿Está seguro de eliminar este perfil?')) return;
    
    try {
      await perfilService.deletePerfil(id);
      toast.success('Perfil eliminado');
      loadData(currentPage, sortBy, direction);
    } catch (error) {
      toast.error('Error al eliminar');
      console.error(error);
    }
  };

  const openModal = (item = null) => {
    setEditingItem(item);
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingItem(null);
  };

  if (loading) return <Loader />;

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-neutral-800">Perfiles</h1>
          <p className="text-neutral-600 mt-1">Gestión de perfiles de cargo</p>
        </div>
        <Button onClick={() => openModal()} className="gap-2">
          <Plus className="w-4 h-4" />
          Nuevo Perfil
        </Button>
      </div>

      <div className="flex justify-between items-center gap-4">
        <div className="flex-1 max-w-md">
          <Input
            placeholder="Buscar perfiles..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            icon={Search}
          />
        </div>
        <Button variant="outline" onClick={() => loadData(currentPage, sortBy, direction)} className="gap-2">
          <RefreshCw className="w-4 h-4" />
          Actualizar
        </Button>
      </div>

      <div className="bg-white rounded-lg shadow-sm border border-neutral-200 overflow-hidden hidden md:block">
        <table className="w-full">
          <thead className="bg-neutral-50 border-b border-neutral-200">
            <tr>
              <th 
                className="text-left p-4 text-sm font-semibold text-neutral-700 cursor-pointer hover:bg-neutral-50 transition-colors"
                onClick={() => handleSort('id')}
              >
                <div className="flex items-center gap-1">
                  ID
                  {sortBy === 'id' && (
                    <span className="text-xs">{direction === 'asc' ? '↑' : '↓'}</span>
                  )}
                </div>
              </th>
              <th 
                className="text-left p-4 text-sm font-semibold text-neutral-700 cursor-pointer hover:bg-neutral-50 transition-colors"
                onClick={() => handleSort('nombre')}
              >
                <div className="flex items-center gap-1">
                  Nombre
                  {sortBy === 'nombre' && (
                    <span className="text-xs">{direction === 'asc' ? '↑' : '↓'}</span>
                  )}
                </div>
              </th>
              <th className="text-left p-4 text-sm font-semibold text-neutral-700">Descripción</th>
              <th className="text-center p-4 text-sm font-semibold text-neutral-700">Exámenes Asociados</th>
              <th className="text-right p-4 text-sm font-semibold text-neutral-700">Acciones</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-neutral-200">
            {perfiles.length === 0 ? (
              <tr>
                <td colSpan="5" className="p-8 text-center text-neutral-500">
                  No se encontraron perfiles
                </td>
              </tr>
            ) : (
              perfiles.map((perfil) => {
                const asociados = perfilesAsociados[perfil.id] || { raven: false, disc: false, ipv: false };
                return (
                  <tr key={perfil.id} className="hover:bg-neutral-50">
                    <td className="p-4 text-sm font-mono text-neutral-600">
                      #{perfil.id}
                    </td>
                    <td className="p-4 font-medium text-neutral-800">{perfil.nombre}</td>
                    <td className="p-4 text-neutral-600">{perfil.descripcion}</td>
                    <td className="p-4">
                      <div className="flex justify-center gap-2">
                        <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${
                          asociados.raven ? 'bg-orange-100 text-orange-700' : 'bg-neutral-100 text-neutral-400'
                        }`}>
                          <Target className="w-3 h-3" />
                          Raven
                        </span>
                        <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${
                          asociados.disc ? 'bg-purple-100 text-purple-700' : 'bg-neutral-100 text-neutral-400'
                        }`}>
                          <Brain className="w-3 h-3" />
                          DISC
                        </span>
                        <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${
                          asociados.ipv ? 'bg-indigo-100 text-indigo-700' : 'bg-neutral-100 text-neutral-400'
                        }`}>
                          <TrendingUp className="w-3 h-3" />
                          IPV
                        </span>
                      </div>
                    </td>
                    <td className="p-4">
                      <div className="flex justify-end gap-2">
                        <button
                          onClick={() => openModal(perfil)}
                          className="p-2 text-primary hover:bg-primary-50 rounded-md transition-colors"
                        >
                          <Edit2 className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDelete(perfil.id)}
                          className="p-2 text-red-600 hover:bg-red-50 rounded-md transition-colors"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {/* Vista de Tarjetas (Móvil) */}
      <div className="md:hidden space-y-3">
        {perfiles.length === 0 ? (
          <div className="text-center py-8 text-neutral-500">
            No se encontraron perfiles
          </div>
        ) : (
          perfiles.map((perfil) => (
            <div key={perfil.id} className="bg-white border border-neutral-200 rounded-lg p-4 shadow-sm">
              <div className="flex justify-between items-start mb-3">
                <div>
                  <h3 className="font-semibold text-neutral-900">{perfil.nombre}</h3>
                  <p className="text-sm text-neutral-600 mt-1">{perfil.descripcion}</p>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => openModal(perfil)}
                    className="p-2 text-neutral-600 hover:text-neutral-900 hover:bg-neutral-100 rounded-md transition-colors"
                    title="Editar"
                  >
                    <Edit2 className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => handleDelete(perfil.id)}
                    className="p-2 text-red-600 hover:text-red-900 hover:bg-red-50 rounded-md transition-colors"
                    title="Eliminar"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
              
              {/* Exámenes asociados */}
              <div className="flex flex-wrap gap-2">
                {(perfilesAsociados[perfil.id]?.raven) && (
                  <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                    Raven
                  </span>
                )}
                {(perfilesAsociados[perfil.id]?.disc) && (
                  <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                    DISC
                  </span>
                )}
                {(perfilesAsociados[perfil.id]?.ipv) && (
                  <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                    IPV
                  </span>
                )}
              </div>
            </div>
          ))
        )}
      </div>

      {/* Paginación */}
      {totalPages > 1 && (
        <div className="flex justify-center items-center gap-2 mt-6">
          <button
            onClick={() => handlePageChange(currentPage - 1)}
            disabled={currentPage === 0}
            className="px-3 py-2 text-sm font-medium text-neutral-500 bg-white border border-neutral-300 rounded-md hover:bg-neutral-50 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Anterior
          </button>
          
          {Array.from({ length: totalPages }, (_, i) => i).map((page) => (
            <button
              key={page}
              onClick={() => handlePageChange(page)}
              className={`px-3 py-2 text-sm font-medium rounded-md ${
                currentPage === page
                  ? 'text-white border'
                  : 'text-neutral-500 bg-white border border-neutral-300 hover:bg-neutral-50'
              }`}
              style={currentPage === page ? { backgroundColor: '#297373', borderColor: '#297373' } : {}}
            >
              {page + 1}
            </button>
          ))}
          
          <button
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={currentPage === totalPages - 1}
            className="px-3 py-2 text-sm font-medium text-neutral-500 bg-white border border-neutral-300 rounded-md hover:bg-neutral-50 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Siguiente
          </button>
        </div>
      )}

      {/* Información de paginación */}
      <div className="text-center text-sm text-neutral-600 mt-2">
        Mostrando {perfiles.length} de {totalElements} perfiles
      </div>

      <PerfilModal
        isOpen={showModal}
        onClose={closeModal}
        onSave={handleSave}
        initialData={editingItem}
        examenRaven={examenRaven}
        examenDisc={examenDisc}
        examenIpv={examenIpv}
        asociacionesActuales={editingItem ? perfilesAsociados[editingItem.id] : null}
      />
    </div>
  );
}

function PerfilModal({ isOpen, onClose, onSave, initialData, examenRaven, examenDisc, examenIpv, asociacionesActuales }) {
  const [formData, setFormData] = useState({
    nombre: '',
    descripcion: ''
  });
  const [examenesSeleccionados, setExamenesSeleccionados] = useState({
    raven: false,
    disc: false,
    ipv: false
  });

  useEffect(() => {
    if (initialData) {
      setFormData({
        nombre: initialData.nombre || '',
        descripcion: initialData.descripcion || ''
      });
      // Cargar asociaciones actuales si existen
      setExamenesSeleccionados({
        raven: asociacionesActuales?.raven || false,
        disc: asociacionesActuales?.disc || false,
        ipv: asociacionesActuales?.ipv || false
      });
    } else {
      setFormData({ nombre: '', descripcion: '' });
      setExamenesSeleccionados({ raven: false, disc: false, ipv: false });
    }
  }, [initialData, isOpen, asociacionesActuales]);

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(formData, examenesSeleccionados);
  };

  const toggleExamen = (tipo) => {
    setExamenesSeleccionados(prev => ({
      ...prev,
      [tipo]: !prev[tipo]
    }));
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={initialData ? 'Editar Perfil' : 'Nuevo Perfil'}
      size="lg"
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        <Input
          label="Nombre"
          value={formData.nombre}
          onChange={(e) => setFormData({ ...formData, nombre: e.target.value })}
          required
        />
        <div>
          <label className="block text-sm font-medium text-neutral-700 mb-1">
            Descripción
          </label>
          <textarea
            className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:ring-2 focus:ring-primary focus:border-transparent"
            rows={3}
            value={formData.descripcion}
            onChange={(e) => setFormData({ ...formData, descripcion: e.target.value })}
            required
          />
        </div>

        {/* Sección de Exámenes */}
        <div className="border-t border-neutral-200 pt-4">
          <div className="flex items-center gap-2 mb-3">
            <Link2 className="w-5 h-5 text-neutral-600" />
            <label className="text-sm font-medium text-neutral-700">
              Exámenes Asociados
            </label>
          </div>
          <p className="text-xs text-neutral-500 mb-3">
            Selecciona los exámenes que deben rendir los postulantes con este perfil
          </p>

          <div className="space-y-3">
            {/* Raven */}
            <div
              onClick={() => examenRaven && toggleExamen('raven')}
              className={`flex items-center gap-3 p-4 rounded-lg border-2 cursor-pointer transition-all ${
                !examenRaven 
                  ? 'opacity-50 cursor-not-allowed border-neutral-200 bg-neutral-50' 
                  : examenesSeleccionados.raven
                    ? 'border-orange-500 bg-orange-50'
                    : 'border-neutral-200 hover:border-orange-300 hover:bg-orange-50/50'
              }`}
            >
              <div className="p-2 rounded-lg bg-orange-100">
                <Target className="w-6 h-6 text-orange-600" />
              </div>
              <div className="flex-1">
                <h4 className="font-semibold text-neutral-800">Test Raven</h4>
                <p className="text-xs text-neutral-500">
                  {examenRaven ? examenRaven.nombre : 'No hay examen Raven configurado'}
                </p>
              </div>
              <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center transition-all ${
                examenesSeleccionados.raven
                  ? 'bg-orange-500 border-orange-500'
                  : 'border-neutral-300'
              }`}>
                {examenesSeleccionados.raven && <CheckCircle className="w-4 h-4 text-white" />}
              </div>
            </div>

            {/* DISC */}
            <div
              onClick={() => examenDisc && toggleExamen('disc')}
              className={`flex items-center gap-3 p-4 rounded-lg border-2 cursor-pointer transition-all ${
                !examenDisc 
                  ? 'opacity-50 cursor-not-allowed border-neutral-200 bg-neutral-50' 
                  : examenesSeleccionados.disc
                    ? 'border-purple-500 bg-purple-50'
                    : 'border-neutral-200 hover:border-purple-300 hover:bg-purple-50/50'
              }`}
            >
              <div className="p-2 rounded-lg bg-purple-100">
                <Brain className="w-6 h-6 text-purple-600" />
              </div>
              <div className="flex-1">
                <h4 className="font-semibold text-neutral-800">Test DISC</h4>
                <p className="text-xs text-neutral-500">
                  {examenDisc ? examenDisc.nombre : 'No hay examen DISC configurado'}
                </p>
              </div>
              <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center transition-all ${
                examenesSeleccionados.disc
                  ? 'bg-purple-500 border-purple-500'
                  : 'border-neutral-300'
              }`}>
                {examenesSeleccionados.disc && <CheckCircle className="w-4 h-4 text-white" />}
              </div>
            </div>

            {/* IPV */}
            <div
              onClick={() => examenIpv && toggleExamen('ipv')}
              className={`flex items-center gap-3 p-4 rounded-lg border-2 cursor-pointer transition-all ${
                !examenIpv 
                  ? 'opacity-50 cursor-not-allowed border-neutral-200 bg-neutral-50' 
                  : examenesSeleccionados.ipv
                    ? 'border-indigo-500 bg-indigo-50'
                    : 'border-neutral-200 hover:border-indigo-300 hover:bg-indigo-50/50'
              }`}
            >
              <div className="p-2 rounded-lg bg-indigo-100">
                <TrendingUp className="w-6 h-6 text-indigo-600" />
              </div>
              <div className="flex-1">
                <h4 className="font-semibold text-neutral-800">Examen IPV</h4>
                <p className="text-xs text-neutral-500">
                  {examenIpv ? examenIpv.nombre : 'No hay examen IPV configurado'}
                </p>
              </div>
              <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center transition-all ${
                examenesSeleccionados.ipv
                  ? 'bg-indigo-500 border-indigo-500'
                  : 'border-neutral-300'
              }`}>
                {examenesSeleccionados.ipv && <CheckCircle className="w-4 h-4 text-white" />}
              </div>
            </div>
          </div>

          {/* Contador de seleccionados */}
          <div className="mt-3 text-sm text-neutral-600">
            <span className="font-medium">
              {[examenesSeleccionados.raven, examenesSeleccionados.disc, examenesSeleccionados.ipv].filter(Boolean).length}
            </span> de 3 exámenes seleccionados
          </div>
        </div>

        <div className="flex justify-end gap-2 pt-4">
          <Button type="button" variant="outline" onClick={onClose}>
            Cancelar
          </Button>
          <Button type="submit">
            Guardar
          </Button>
        </div>
      </form>
    </Modal>
  );
}
